import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

type CarrierReviewLabels = {
	rating: string;
	comment: string;
};

const buildCarrierReviewFormSchema = (
	t: TFunction<'translation', undefined>,
	labels: CarrierReviewLabels,
) =>
	z
		.object({
			rating: z.coerce
				.number()
				.min(1, {
					message: t(
						'general.errors.form_validation.select_required',
						'Must select a {{propertyName}}',
						{ propertyName: labels.rating },
					).toString(),
				})
				.max(5),
			comment: z
				.string()
				.max(1000, {
					message: t(
						'general.errors.form_validation.max',
						'{{propertyName}} must be less than {{maxLength}} characters',
						{ propertyName: labels.comment, maxLength: 1000 },
					).toString(),
				})
				.optional(),
		})
		.superRefine((values, context) => {
			if (
				values.rating > 0 &&
				values.rating <= 3 &&
				(!values.comment || values.comment === '')
			) {
				context.addIssue({
					code: z.ZodIssueCode.custom,
					message: t(
						'pages.order.shipment-details.assigned-carrier.review.form.validation.comment_required',
						'Tell us something about your review',
					).toString(),
					path: ['comment'],
				});
			}
		});

export type CarrierReviewFormValues = z.infer<
	ReturnType<typeof buildCarrierReviewFormSchema>
>;

export const useCarrierReviewFormSchema = (labels: CarrierReviewLabels) => {
	const { t } = useTranslation();

	return useMemo(
		() => buildCarrierReviewFormSchema(t, labels),
		[t, labels.rating, labels.comment],
	);
};
